import { AlertCircle, Check } from '@untitled-ui/icons-react';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Decorator, FeaturedIcon } from '@myhealth-raccoon/components';
import { validation } from '@myhealth-raccoon/forms';

import { InlineModal, InlineModalProps } from '../InlineModal';
import { VisitInfo } from 'src/services/auth';

type Props = {
    onSubmit(eligible: boolean, exemptions?: string[]): void;
    visitInfo?: VisitInfo;
    validMedicare?: boolean;
} & Omit<InlineModalProps, 'className' | 'title'>;

type FormValues = {
    exemptions?: string[];
    none?: 'on' | false;
    noAnswer?: 'on' | false;
};

type FormValues2 = {
    feeAcknowledgement?: boolean;
};

export function BulkBilledTelehealthEligibility({ onSubmit, visitInfo, validMedicare, isOpen, onClose }: Props) {
    const { register, setValue, reset, handleSubmit, watch } = useForm<FormValues>();

    const {
        register: register2,
        handleSubmit: handleSubmit2,
        formState: { errors: errors2 },
    } = useForm<FormValues2>();

    const [selectedExemptions, setExemptions] = useState<string[]>();

    const exemption = (value: string, id?: string) => {
        return (
            <fieldset className="flex flex-row items-start gap-2">
                <input
                    id={id || value}
                    type="checkbox"
                    className="checkbox mt-0.5"
                    value={value}
                    {...register('exemptions', {
                        onChange() {
                            setValue('noAnswer', false);
                            setValue('none', false);
                        },
                    })}
                />
                <label htmlFor={id || value}>{value}</label>
            </fieldset>
        );
    };

    const isEligible = useMemo(() => {
        if (selectedExemptions && selectedExemptions.length) {
            return true;
        }
        if (
            validMedicare &&
            visitInfo &&
            visitInfo.lastF2FDate &&
            moment().diff(moment(visitInfo.lastF2FDate.toString()).format('YYYY-MM-DD'), 'months') <= 12
        ) {
            return true;
        }
        return false;
    }, [selectedExemptions, validMedicare, visitInfo]);

    return (
        <>
            <InlineModal
                isOpen={isOpen && !selectedExemptions}
                onClose={onClose}
                title={
                    <div className="text-sm font-normal text-gray-600">
                        <h1 className="text-3xl font-semibold text-gray-900">
                            Is this consultation related to any of the following?
                        </h1>
                        <p className="mt-1">Select all that apply.</p>
                    </div>
                }
            >
                <form
                    onSubmit={handleSubmit((values) => {
                        setExemptions(values.exemptions ?? []);
                    })}
                >
                    <div className="mx-5 flex flex-col gap-5 rounded-lg border bg-gray-50 p-4">
                        {exemption('Sexual health consultation')}
                        {exemption('Reproductive health consultation')}
                        {exemption('Blood-borne virus consultation (i.e. HIV, Hepatitis)')}
                        {exemption('Experiencing hardship, including domestic/family violence')}
                        or
                        <fieldset className="flex flex-row items-start gap-2">
                            <input
                                className="radio mt-0.5"
                                type="radio"
                                id="none"
                                {...register('none', {
                                    onChange(event) {
                                        if (event.target.checked) {
                                            reset({ none: 'on', noAnswer: false, exemptions: [] });
                                        }
                                    },
                                })}
                            />
                            <label htmlFor="none">None of these</label>
                        </fieldset>
                        <fieldset className="flex flex-row items-start gap-2">
                            <input
                                className="radio mt-0.5"
                                type="radio"
                                id="none"
                                {...register('noAnswer', {
                                    onChange(event) {
                                        if (event.target.checked) {
                                            reset({ noAnswer: 'on', none: false, exemptions: [] });
                                        }
                                    },
                                })}
                            />
                            <label htmlFor="none">Prefer not to answer</label>
                        </fieldset>
                    </div>
                    <hr className="my-5" />
                    <div className="flex flex-row gap-3 px-6 pb-6">
                        {onClose && (
                            <Button className="flex-1" onClick={() => onClose(false)} color="gray" type="button">
                                Back
                            </Button>
                        )}
                        <Button
                            className="flex-1"
                            type="submit"
                            disabled={!watch('noAnswer') && !watch('none') && !watch('exemptions')?.length}
                        >
                            Check eligibility
                        </Button>
                    </div>
                </form>
            </InlineModal>
            <InlineModal
                isOpen={isOpen && !!selectedExemptions}
                onClose={onClose}
                title={
                    <div className="text-sm font-normal text-gray-600">
                        <h1 className="text-3xl font-semibold text-gray-900">Eligibility check</h1>
                        <p className="mt-1">Thanks for providing the required information.</p>
                    </div>
                }
            >
                <form onSubmit={handleSubmit2(() => onSubmit(isEligible, selectedExemptions))}>
                    <div className="space-y-5 px-5">
                        {!isEligible ? (
                            <>
                                <div className="bg-warning-25 space-y-5 rounded-lg p-4 text-center">
                                    <FeaturedIcon
                                        icon={AlertCircle}
                                        color="warning"
                                        variant="outline"
                                        size="xl"
                                        className="mx-auto"
                                    />
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        You may not be eligible for bulk billing.
                                    </h3>

                                    <p>
                                        As we couldn’t verify a valid Medicare or concession card, this appointment will
                                        be billed privately and won’t be eligible for a Medicare rebate.{' '}
                                        <a
                                            href="https://www.myhealth.net.au/blog/bulk-billing-incentive/"
                                            target="_blank"
                                            className="text-primary font-semibold hover:underline"
                                        >
                                            Learn more
                                        </a>
                                    </p>
                                </div>
                                <p>
                                    Telehealth rebates are only available to patients who meet specific eligibility
                                    criteria. You may contact the clinic to discuss your eligibility, or continue with
                                    your booking.
                                </p>
                            </>
                        ) : (
                            <>
                                <div className="bg-success-25 space-y-5 rounded-lg p-4 text-center">
                                    <FeaturedIcon
                                        icon={Check}
                                        color="success"
                                        variant="outline"
                                        size="xl"
                                        className="mx-auto"
                                    />
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        You’re eligible for bulk billing.
                                    </h3>

                                    <p>Based on what you’ve told us, you’re eligible for Medicare bulk billing.</p>
                                    <p>
                                        <strong>Please note:</strong> If there are changes to your circumstances or
                                        eligibility, the clinic may contact you, and fees could apply.{' '}
                                        <a
                                            href="https://www.myhealth.net.au/blog/bulk-billing-incentive/"
                                            target="_blank"
                                            className="text-primary font-semibold hover:underline"
                                        >
                                            Learn more
                                        </a>
                                    </p>
                                </div>
                                <div className="border-error ring-error rounded border p-4 ring-2 ring-offset-1">
                                    <strong className="text-gray-900">What this means for you:</strong>
                                    <ul className="mt-3 flex flex-col gap-3">
                                        <li className="flex flex-row items-start gap-2">
                                            <Check className="text-success-600 size-6" /> No out of pocket costs for
                                            bulk-billing consultation
                                        </li>
                                        <li className="flex flex-row items-start gap-2">
                                            <Check className="text-success-600 size-6" /> Medicare rebates applied
                                            automatically
                                        </li>
                                    </ul>
                                </div>
                                <Decorator fieldError={errors2.feeAcknowledgement}>
                                    <input
                                        {...register2('feeAcknowledgement', { validate: validation.required() })}
                                        type="checkbox"
                                        id="acknowledgement"
                                        className="checkbox -mt-0.5 mr-2"
                                    />
                                    <label htmlFor="acknowledgement">
                                        I understand that fees may apply if bulk billing fails or my eligibility
                                        changes.
                                    </label>
                                </Decorator>
                            </>
                        )}
                    </div>
                    <hr className="my-5" />
                    <div className="flex flex-row gap-3 px-6 pb-6">
                        <Button
                            className="flex-1"
                            onClick={() => {
                                setExemptions(undefined);
                            }}
                            color="gray"
                            type="button"
                        >
                            Back
                        </Button>
                        <Button className="flex-1" type="submit">
                            Continue Booking
                        </Button>
                    </div>
                </form>
            </InlineModal>
        </>
    );
}
